"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useState, useRef, useId, useCallback, useLayoutEffect, useEffect, cloneElement } from 'react';
import FocusLock from 'react-focus-lock';
import Close from '../../icons/component-icons/close/index.js';
import { Portal } from '../../portal/portal/index.js';
import styles from './modal.module.scss.js';
import getOS from '../../utilities/check-os/index.js';
import checkIfMobileDevice from '../../utilities/check-device/index.js';
import getBrowser from '../../utilities/check-browser/index.js';
import calculateRem from '../../utilities/calculate-rem/index.js';
import useViewport from '../../utilities/use-viewport/index.js';
import smoothScroll from '../../utilities/scroll-animate/index.js';
import '../../buttons/button/index.js';
import '../../buttons/text-link/index.js';
import '../../icons/component-icons/left-caret-bold/index.js';
import '../../icons/component-icons/right-caret-bold/index.js';
import { ButtonIcon } from '../../buttons/button-icon/index.js';
import { Scrollable } from '../../scrollable/scrollable/index.js';
import scroll from './util/scroll-lock.js';
import screenReader from './util/screen-reader.js';
import ModalTitle from './modal-title.js';
import ModalFooter from './modal-footer.js';
import ModalBody from './modal-body.js';

const scrollableDefaultValue = {
    overflowOrientation: 'vertical',
    verticalScrollbarInset: {
        top: 'var(--vds-space-8x)',
        right: '3px',
        bottom: 'var(--vds-space-8x)',
        left: '0px',
    },
    horizontalScrollbarInset: {
        top: '0px',
        right: 'var(--vds-space-8x)',
        bottom: '3px',
        left: 'var(--vds-space-8x)',
    },
    scrollContainerInset: {
        top: '0px',
        right: 'var(--vds-space-8x)',
        bottom: '0px',
        left: 'var(--vds-space-8x)',
    },
    contentMargin: {
        top: 'var(--vds-space-8x)',
        bottom: 'var(--vds-space-8x)',
        right: '0px',
        left: '0px',
    },
};
/**
 * @component Modal
 * @package Modals
 * @document ../_docs/code/Modal.editor.tsx
 * @document ../_docs/code/Modal.snippet.tsx
 * @document ../_docs/code/Modal.import.tsx
 */
const Modal = (_a) => {
    var { disableOutsideClick = false, zIndex = 999, viewport, fullscreen = false, disableAnimation = false, closeButtonId, enableBackButton = false, maxHeight = '90vh', maxWidth = '90vw', toggleButton, opened: openedProp, onOpenedChange, 'data-analyticstrack': analyticsTrack, 'data-track': track, 'data-track-ignore': ignoreTrack, 'data-clickstream': clickStream, id, closeButton = {
        show: true,
    }, height, width, contentContainerId, ariaLabel, 'aria-label': ariaLabelValue, children, title, buttonGroup, colorMode = 'light', scrollable = scrollableDefaultValue } = _a, rest = __rest(_a, ["disableOutsideClick", "zIndex", "viewport", "fullscreen", "disableAnimation", "closeButtonId", "enableBackButton", "maxHeight", "maxWidth", "toggleButton", "opened", "onOpenedChange", 'data-analyticstrack', 'data-track', 'data-track-ignore', 'data-clickstream', "id", "closeButton", "height", "width", "contentContainerId", "ariaLabel", 'aria-label', "children", "title", "buttonGroup", "colorMode", "scrollable"]);
    const [opened, setOpened] = useState(openedProp || false);
    const [unmounted, setUnmounted] = useState(false);
    const [modalScrollHeight, setModalScrollHeight] = useState(0);
    const [desiredContentHeight, setDesiredContentHeight] = useState(0);
    const [childContainerHeight, setChildContainerHeight] = useState(0);
    const [dialogContainerHeight, setDialogContainerHeight] = useState(0);
    const viewportHookValue = useViewport();
    const modalId = useRef(null);
    const scrollTopRef = useRef(null);
    const _useId = useId();
    if (modalId.current === null || modalId.current === undefined) {
        modalId.current = id || _useId;
    }
    const scrollbarId = `${modalId.current}-scrollbar`;
    const closeId = closeButtonId || `${modalId.current}-close-button`;
    const isAppleDevice = getOS() === 'ios';
    const isMobileDevice = checkIfMobileDevice();
    const isFirefox = getBrowser() === 'firefox';
    const mobile = viewport === 'mobile' || viewportHookValue === 'mobile';
    const historyBackTimer = useRef(null);
    const unmountTimer = useRef(null);
    const fullscreenDelayTimer = useRef(null);
    const toggleButtonRef = useRef(null);
    const overlayRef = useRef(null);
    const dialogInnerWrapperRef = useRef(null);
    const contentContainerRef = useRef(null);
    const childContentResizeObserver = useRef(null);
    const contentContainerHeightSettingTimer = useRef(null);
    const calcScrollHeight = () => {
        var _a;
        /* istanbul ignore else */
        if (contentContainerRef.current) {
            const currentClientHeight = (_a = contentContainerRef.current.clientHeight) !== null && _a !== void 0 ? _a : 0;
            setModalScrollHeight((currentClientHeight <= desiredContentHeight
                ? calculateRem(currentClientHeight)
                : 0));
        }
    };
    const overlayStyles = [styles.overlay];
    const dialogStyles = [styles.dialog];
    const dialogInnerWrapperStyles = [styles.dialogInnerWrapper];
    const scrollContainerWrapperStyles = [styles.scrollContainerWrapper];
    const contentContainerStyles = [styles.contentContainer];
    const closeButtonWrapper = [styles.closeButtonWrapper];
    const closeButtonBgLayerStyles = [styles.closeButtonBgLayer];
    if (colorMode === 'dark') {
        dialogStyles.push(styles.dark);
        closeButtonWrapper.push(styles.dark);
        closeButtonBgLayerStyles.push(styles.dark);
    }
    if (disableAnimation)
        dialogStyles.push(styles.disableAnimation);
    if (viewport !== undefined) {
        dialogStyles.push(styles[viewport]);
        dialogInnerWrapperStyles.push(styles[viewport]);
        contentContainerStyles.push(styles[viewport]);
    }
    if (opened) {
        dialogStyles.push(styles.opened);
        overlayStyles.push(styles.opened);
    }
    if (unmounted) {
        dialogStyles.push(styles.unmounted);
        overlayStyles.push(styles.unmounted);
    }
    if (fullscreen) {
        dialogStyles.push(styles.fullscreen);
        overlayStyles.push(styles.fullscreen);
        dialogInnerWrapperStyles.push(styles.fullscreen);
    }
    if (closeButton && closeButton.show === false) {
        contentContainerStyles.push(styles.noClosebutton);
    }
    dialogStyles.push(rest.className);
    if (isMobileDevice) {
        closeButtonWrapper.push(styles.mobile);
        dialogStyles.push(styles.mobile);
        dialogInnerWrapperStyles.push(styles.mobile);
    }
    const _onUnload = useCallback(() => {
        /* istanbul ignore else */
        setUnmounted(true);
        clearTimeout(unmountTimer.current);
        unmountTimer.current = setTimeout(() => {
            setOpened(false);
            // trigger onOpenedChange when modal is closed using browser back button click
            /* istanbul ignore else */
            if (!enableBackButton && onOpenedChange) {
                onOpenedChange(false);
            }
        }, disableAnimation ? 0 : 390);
    }, [disableAnimation, enableBackButton, onOpenedChange]);
    const _registerOverride = useCallback(() => {
        var _a;
        // if enableBackButton is true, browser back will close the modal and navigate to the previous page
        /* istanbul ignore else */
        // eslint-disable-next-line
        if (!enableBackButton)
            history === null || history === void 0 ? void 0 : history.pushState(null, '');
        /* istanbul ignore else */
        if (typeof window !== 'undefined') {
            (_a = window.addEventListener) === null || _a === void 0 ? void 0 : _a.call(window, 'popstate', _onUnload);
        }
    }, [_onUnload, enableBackButton]);
    const _unRegisterOverride = useCallback(() => {
        var _a;
        /* istanbul ignore else */
        if (typeof window !== 'undefined') {
            (_a = window.removeEventListener) === null || _a === void 0 ? void 0 : _a.call(window, 'popstate', _onUnload);
        }
    }, [_onUnload]);
    const _onOpenedChanged = useCallback(() => {
        /* istanbul ignore else */
        if (onOpenedChange) {
            onOpenedChange(!opened);
        }
    }, [onOpenedChange, opened]);
    const _toggleModal = useCallback(() => {
        _onOpenedChanged();
        clearTimeout(historyBackTimer.current);
        clearTimeout(unmountTimer.current);
        /* istanbul ignore else */
        if (opened) {
            setUnmounted(true);
            unmountTimer.current = setTimeout(() => {
                setOpened(false);
                /* istanbul ignore else */
                if (toggleButton &&
                    toggleButtonRef.current &&
                    typeof document !== 'undefined') {
                    toggleButtonRef.current.focus();
                }
            }, disableAnimation ? 0 : 390);
            _unRegisterOverride();
            historyBackTimer.current = setTimeout(() => {
                // if enableBackButton is true and null is added to browser history, history.back will skip the null value added on opening of the modal.
                /* istanbul ignore else */
                // eslint-disable-next-line no-restricted-globals
                if (!enableBackButton)
                    history.back();
            }, disableAnimation ? 0 : 390);
            return;
        }
        _registerOverride();
        setUnmounted(false);
        setOpened(true);
    }, [
        _onOpenedChanged,
        _registerOverride,
        _unRegisterOverride,
        disableAnimation,
        enableBackButton,
        opened,
        toggleButton,
    ]);
    const _onKeyDownDocument = useCallback((e) => {
        /* istanbul ignore else */
        if (e.key === 'Escape') {
            _toggleModal();
        }
    }, [_toggleModal]);
    const _resizeHandler = useCallback(() => {
        var _a, _b;
        const dialogHeight = (_a = dialogInnerWrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height;
        const childHeight = (_b = contentContainerRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height;
        if (dialogHeight && dialogHeight !== dialogContainerHeight) {
            setDialogContainerHeight(dialogHeight);
        }
        if (childHeight && childHeight !== childContainerHeight) {
            setChildContainerHeight(childHeight);
        }
    }, [childContainerHeight, dialogContainerHeight]);
    useLayoutEffect(() => {
        if (!opened) {
            return;
        }
        if (contentContainerHeightSettingTimer.current) {
            clearTimeout(contentContainerHeightSettingTimer.current);
        }
        contentContainerHeightSettingTimer.current = setTimeout(() => {
            var _a, _b;
            if (contentContainerRef.current) {
                if (childContentResizeObserver.current === null) {
                    childContentResizeObserver.current = (typeof window !== 'undefined' &&
                        new ResizeObserver(() => {
                            window.requestAnimationFrame(() => {
                                var _a;
                                const childHeight = (_a = contentContainerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height;
                                /* istanbul ignore else */
                                if (childHeight &&
                                    childContainerHeight &&
                                    childHeight > childContainerHeight) {
                                    setChildContainerHeight(childHeight);
                                }
                                calcScrollHeight();
                            });
                        }));
                    if (childContentResizeObserver.current &&
                        contentContainerRef.current) {
                        (_b = (_a = childContentResizeObserver.current) === null || _a === void 0 ? void 0 : _a.observe) === null || _b === void 0 ? void 0 : _b.call(_a, contentContainerRef.current);
                    }
                }
            }
        }, 0);
        return () => {
            var _a;
            if (childContentResizeObserver.current) {
                if (contentContainerRef.current) {
                    (_a = childContentResizeObserver.current) === null || _a === void 0 ? void 0 : _a.unobserve(
                    // eslint-disable-next-line react-hooks/exhaustive-deps
                    contentContainerRef.current);
                }
                childContentResizeObserver.current = null;
            }
        };
    }, [opened, childContainerHeight]);
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', _resizeHandler);
        }
        if (typeof document !== 'undefined') {
            document.addEventListener('fullscreenchange', () => {
                if (document.fullscreenElement === null) {
                    if (fullscreenDelayTimer.current)
                        clearTimeout(fullscreenDelayTimer.current);
                    fullscreenDelayTimer.current = setTimeout(() => {
                        calcScrollHeight();
                    }, 100);
                }
            });
        }
        if (openedProp) {
            _registerOverride();
        }
        return () => {
            scroll.disable();
            /* istanbul ignore else */
            if (isMobileDevice && !isAppleDevice)
                screenReader.CloseDialog();
            window.removeEventListener('resize', _resizeHandler);
        };
    }, [
        _registerOverride,
        _resizeHandler,
        isAppleDevice,
        isMobileDevice,
        openedProp,
    ]);
    // eslint-disable-next-line no-shadow
    const _calculateContentHeightValue = (maxHeight) => {
        const number = parseInt(maxHeight, 10);
        const units = String(maxHeight).replace(/^[\d.-]+/, '');
        const containerPadding = 64; // Constant for container padding top & bottom
        const minHeightAdjusted = 232 - containerPadding; // Constant for the minimum height after adjustment
        let calculatedHeight;
        if (units === 'vh' || units === '%') {
            calculatedHeight =
                (document.documentElement.clientHeight * number) / 100 -
                    containerPadding;
        }
        else if (units === 'rem' || units === 'em') {
            calculatedHeight = number * 16 - containerPadding; // Using default for simplification
        }
        else {
            calculatedHeight = number - containerPadding;
        }
        return Math.max(calculatedHeight, minHeightAdjusted);
    };
    useEffect(() => {
        if (height) {
            setDesiredContentHeight(Math.min(_calculateContentHeightValue(height), _calculateContentHeightValue(maxHeight || '90vh')));
        }
        else {
            setDesiredContentHeight(_calculateContentHeightValue(maxHeight || '90vh'));
        }
    }, [height, maxHeight]);
    useEffect(() => {
        // Enables body scroll locking for mobile devices
        /**
         * NOTE: in vds-gen3, this is set in componentDidMount and componentWillUnmount
         * because it's done in ModalBase used in Modal to mount/unmount the actual Modal
         *
         * In gen3-rsc, this is condensed to just having the Modal in one file so can just check openend state to enable/disable scroll-lock
         */
        if (opened) {
            scroll.enable();
            document.addEventListener('keydown', _onKeyDownDocument);
            // Fix talkback on Android devices
            /* istanbul ignore else */
            if (isMobileDevice && !isAppleDevice)
                screenReader.OpenDialog(`${modalId.current}-portal`);
        }
        else {
            document.removeEventListener('keydown', _onKeyDownDocument);
            scroll.disable();
            /* istanbul ignore else */
            if (isMobileDevice && !isAppleDevice)
                screenReader.CloseDialog();
        }
        return () => document.removeEventListener('keydown', _onKeyDownDocument);
    }, [opened, _onKeyDownDocument, isAppleDevice, isMobileDevice]);
    useEffect(() => {
        if (openedProp !== undefined && opened !== openedProp) {
            _toggleModal();
        }
    }, [openedProp]);
    useEffect(() => {
        var _a, _b;
        if (childContentResizeObserver.current && contentContainerRef.current) {
            (_b = (_a = childContentResizeObserver.current) === null || _a === void 0 ? void 0 : _a.observe) === null || _b === void 0 ? void 0 : _b.call(_a, contentContainerRef.current);
        }
    }, [childContainerHeight, dialogContainerHeight]);
    const _onClickOverlay = (e) => {
        if (!disableOutsideClick &&
            e.target.contains(overlayRef.current)) {
            _toggleModal();
        }
    };
    const _onClickToggleButton = () => {
        var _a, _b;
        _toggleModal();
        if ((_a = toggleButton.props) === null || _a === void 0 ? void 0 : _a.onClick)
            (_b = toggleButton.props) === null || _b === void 0 ? void 0 : _b.onClick();
    };
    const _onKeyDownToggleButton = (e) => {
        var _a, _b;
        /* istanbul ignore else */
        if (e.keyCode === 13 || e.keyCode === 32) {
            e.preventDefault();
            e.stopPropagation();
            (_b = (_a = toggleButton === null || toggleButton === void 0 ? void 0 : toggleButton.props) === null || _a === void 0 ? void 0 : _a.onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e);
            _onClickToggleButton();
        }
    };
    const _renderToggle = () => cloneElement(toggleButton, {
        onClick: _onClickToggleButton,
        onKeyDown: _onKeyDownToggleButton,
        ref: toggleButtonRef,
    });
    const _onKeyDownCloseButton = (e) => {
        var _a;
        const arrowDown = e.key === 'ArrowDown';
        const arrowUp = e.key === 'ArrowUp';
        const space = e.key === 'ArrowDown';
        const enter = e.key === 'Enter';
        const offset = 45; // Your desired scroll amount
        const animationDuration = 150; // milliseconds for the scroll animation
        const scrollView = typeof document !== 'undefined'
            ? document.getElementById(`scrollable-content-${scrollbarId}`)
            : null;
        /* istanbul ignore else */
        if (scrollView) {
            let newTargetScrollTop = scrollView.scrollTop;
            if (arrowDown) {
                newTargetScrollTop += offset;
            }
            else if (arrowUp) {
                newTargetScrollTop -= offset;
            }
            // Call the utility function to perform the smooth scroll
            smoothScroll(scrollView, newTargetScrollTop, animationDuration);
        }
        // Do this for firefox for active state because keying down doesn't trigger pseudoclass :active
        // So we add a custom class active then trigger the same effects for pseudoclas :active
        /* istanbul ignore else */
        if ((space || enter) && isFirefox) {
            const btn = typeof document !== 'undefined'
                ? (_a = document === null || document === void 0 ? void 0 : document.getElementById(closeId)) === null || _a === void 0 ? void 0 : _a.classList
                : null;
            btn === null || btn === void 0 ? void 0 : btn.add('active');
        }
    };
    const _calculateAriaLabel = () => {
        switch (true) {
            case !!ariaLabel:
                return `${ariaLabel} close`;
            case !!ariaLabelValue:
                return `${ariaLabelValue} close`;
            default:
                return 'Close';
        }
    };
    const _renderCloseButton = () => {
        if (closeButton && closeButton.show === false) {
            return null;
        }
        const _buttonIconClick = (e) => {
            if (closeButton.onClick) {
                closeButton.onClick(e);
            }
            _toggleModal();
        };
        return (jsxs("div", { className: closeButtonWrapper.join(' '), children: [jsx("span", { id: `${closeId}-BgLayer`, className: closeButtonBgLayerStyles.join(' ') }), jsx(ButtonIcon, Object.assign({}, closeButton, { kind: "ghost", size: mobile ? '36px' : '40px', surface: colorMode === 'light' ? 'lightPrimary' : 'darkPrimary', renderIcon: () => (jsx(Close, { color: colorMode === 'light'
                            ? 'var(--vds-modal-closebutton-background-color-unscrolled-ondarksecondary)'
                            : 'var(--vds-modal-closebutton-background-color-unscrolled-onlightprimary)', size: "medium" })), tabIndex: 0, id: closeId, ariaLabel: _calculateAriaLabel(), "data-testid": "modal-close-button", onKeyDown: _onKeyDownCloseButton, onClick: (e) => _buttonIconClick(e) }))] }));
    };
    const _handleScroll = useCallback((_scrollTop) => {
        var _a, _b;
        const dialogHeight = ((_a = dialogInnerWrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height) || 0;
        const childHeight = ((_b = contentContainerRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().height) || 0;
        const diff = childHeight - dialogHeight;
        if (diff > 32) {
            const el = document.getElementById(`${closeId}-BgLayer`);
            if (el) {
                if (colorMode === 'dark') {
                    el.style.backgroundColor = `var(--vds-modal-closebutton-background-color-scrolled-ondarksecondary)`;
                    if (_scrollTop <= 32) {
                        el.style.opacity = (_scrollTop / 32).toString();
                    }
                    else {
                        el.style.opacity = '1';
                    }
                }
                else if (colorMode === 'light') {
                    el.style.backgroundColor = `var(--vds-modal-closebutton-background-color-scrolled-onlightprimary)`;
                    if (_scrollTop <= 32) {
                        el.style.opacity = (_scrollTop / 32).toString();
                    }
                    else {
                        el.style.opacity = '1';
                    }
                }
            }
        }
    }, [closeId, colorMode]);
    useEffect(() => {
        if (scrollTopRef.current !== null)
            _handleScroll(scrollTopRef.current);
    }, [colorMode, _handleScroll]);
    const _calculateMaxWidth = () => {
        if (fullscreen) {
            return '100vw';
        }
        if (!isMobileDevice && !mobile) {
            return maxWidth;
        }
        return undefined;
    };
    const _calculateScrollContainerHeight = () => {
        if (fullscreen)
            return 'calc(100vh - 96px)';
        if (modalScrollHeight)
            return modalScrollHeight.toString();
        return calculateRem(desiredContentHeight);
    };
    const _calculateScrollable = () => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        if (!scrollable || Object.keys(scrollable).length === 0) {
            return scrollableDefaultValue;
        }
        return {
            overflowOrientation: scrollable.overflowOrientation
                ? scrollable.overflowOrientation
                : 'vertical',
            verticalScrollbarInset: {
                top: ((_a = scrollable.verticalScrollbarInset) === null || _a === void 0 ? void 0 : _a.top)
                    ? scrollable.verticalScrollbarInset.top
                    : 'var(--vds-space-8x)',
                right: ((_b = scrollable.verticalScrollbarInset) === null || _b === void 0 ? void 0 : _b.right)
                    ? scrollable.verticalScrollbarInset.right
                    : '3px',
                bottom: ((_c = scrollable.verticalScrollbarInset) === null || _c === void 0 ? void 0 : _c.bottom)
                    ? scrollable.verticalScrollbarInset.bottom
                    : 'var(--vds-space-8x)',
                left: ((_d = scrollable.verticalScrollbarInset) === null || _d === void 0 ? void 0 : _d.left)
                    ? scrollable.verticalScrollbarInset.left
                    : '0px',
            },
            horizontalScrollbarInset: {
                top: ((_e = scrollable.horizontalScrollbarInset) === null || _e === void 0 ? void 0 : _e.top)
                    ? scrollable.horizontalScrollbarInset.top
                    : '0px',
                right: ((_f = scrollable.horizontalScrollbarInset) === null || _f === void 0 ? void 0 : _f.right)
                    ? scrollable.horizontalScrollbarInset.right
                    : 'var(--vds-space-8x)',
                bottom: ((_g = scrollable.horizontalScrollbarInset) === null || _g === void 0 ? void 0 : _g.bottom)
                    ? scrollable.horizontalScrollbarInset.bottom
                    : '3px',
                left: ((_h = scrollable.horizontalScrollbarInset) === null || _h === void 0 ? void 0 : _h.left)
                    ? scrollable.horizontalScrollbarInset.left
                    : 'var(--vds-space-8x)',
            },
            scrollContainerInset: {
                top: ((_j = scrollable.scrollContainerInset) === null || _j === void 0 ? void 0 : _j.top)
                    ? scrollable.scrollContainerInset.top
                    : '0px',
                right: ((_k = scrollable.scrollContainerInset) === null || _k === void 0 ? void 0 : _k.right)
                    ? scrollable.scrollContainerInset.right
                    : 'var(--vds-space-8x)',
                bottom: ((_l = scrollable.scrollContainerInset) === null || _l === void 0 ? void 0 : _l.bottom)
                    ? scrollable.scrollContainerInset.bottom
                    : '0px',
                left: ((_m = scrollable.scrollContainerInset) === null || _m === void 0 ? void 0 : _m.left)
                    ? scrollable.scrollContainerInset.left
                    : 'var(--vds-space-8x)',
            },
            contentMargin: {
                top: ((_o = scrollable.contentMargin) === null || _o === void 0 ? void 0 : _o.top)
                    ? scrollable.contentMargin.top
                    : 'var(--vds-space-8x)',
                right: ((_p = scrollable.contentMargin) === null || _p === void 0 ? void 0 : _p.right)
                    ? scrollable.contentMargin.right
                    : '0px',
                bottom: ((_q = scrollable.contentMargin) === null || _q === void 0 ? void 0 : _q.bottom)
                    ? scrollable.contentMargin.bottom
                    : 'var(--vds-space-8x)',
                left: ((_r = scrollable.contentMargin) === null || _r === void 0 ? void 0 : _r.left)
                    ? scrollable.contentMargin.left
                    : '0px',
            },
        };
    };
    const _renderContent = () => {
        const noCloseButton = closeButton && closeButton.show === false;
        const footerIndex = 0;
        const scrollContent = (jsxs("div", { className: contentContainerStyles.join(' '), ref: contentContainerRef, children: [!!title && (jsx(ModalTitle, Object.assign({}, title, { noCloseButton: noCloseButton, colorMode: colorMode, children: title.children }))), jsx(ModalBody, { title: title, noCloseButton: noCloseButton, colorMode: colorMode, children: children }), !!buttonGroup && (jsx(ModalFooter, { buttonGroup: buttonGroup, footerIndex: footerIndex + 1, noCloseButton: noCloseButton, isMobileDevice: isMobileDevice, toggleModal: _toggleModal, colorMode: colorMode }))] }));
        return mobile || fullscreen ? (scrollContent) : (jsx("div", { className: scrollContainerWrapperStyles.join(' '), children: jsx(Scrollable, Object.assign({ minHeight: calculateRem('136px'), containerHeight: _calculateScrollContainerHeight(), containerWidth: "100%", contentStyle: {
                    display: 'flex',
                    flexDirection: 'column',
                }, onScroll: (e) => {
                    var _a, _b;
                    scrollTopRef.current = ((_a = e.target) === null || _a === void 0 ? void 0 : _a.scrollTop) || 0;
                    _handleScroll((_b = e.target) === null || _b === void 0 ? void 0 : _b.scrollTop);
                }, id: contentContainerId, scrollableId: scrollbarId, surface: colorMode === 'dark' ? 'darkPrimary' : 'lightPrimary', tabIndex: -1 }, _calculateScrollable(), { children: scrollContent })) }));
    };
    const getDialogInlineStyles = () => {
        const _style = {
            '--animation-distance': '0px',
            '--mobile-height': 0,
            maxWidth: _calculateMaxWidth(),
            maxHeight: maxHeight || '90vh',
        };
        if (typeof window !== 'undefined') {
            _style['--animation-distance'] = `${0.08 * Math.min(window.innerWidth, window.innerHeight)}px`;
            _style['--mobile-height'] = window.innerHeight;
        }
        if (!isMobileDevice && !mobile) {
            if (width) {
                _style.width = width;
            }
        }
        if (fullscreen) {
            _style.maxWidth = '100vw';
            _style.maxHeight = '100vh';
        }
        return _style;
    };
    return (jsxs(Fragment, { children: [toggleButton && _renderToggle(), opened && (jsx(Portal, { id: `${modalId.current}-portal`, children: jsx("div", { className: styles.componentContainer, id: id, style: { zIndex }, children: jsx(FocusLock, { noFocusGuards: isMobileDevice && !isAppleDevice, returnFocus: true, children: jsx("div", { className: overlayStyles.join(' '), onMouseDown: _onClickOverlay, ref: overlayRef, children: jsxs("div", { className: dialogStyles.join(' ').trim(), style: getDialogInlineStyles(), role: "dialog", "aria-modal": "true", "data-analyticstrack": analyticsTrack, "data-track": track, "data-track-ignore": ignoreTrack, "data-clickstream": clickStream, children: [_renderCloseButton(), jsx("div", { className: dialogInnerWrapperStyles.join(' '), id: mobile ? contentContainerId : undefined, ref: dialogInnerWrapperRef, onScroll: (e) => _handleScroll(e.target.scrollTop), children: _renderContent() })] }) }) }) }) }))] }));
};

export { Modal, Modal as default };
